import { MAX_AVAILABLE_ZOOM_LEVEL, MAX_ZOOM_FOR_SINGLE_RESULT } from '../../../constants/dealerLocator.constants';
import { maxZoomSize, minZoomSize } from '../../../constants/map.constatnts';
import { TSitecoreContext, TStringField } from '../../../types/types';
import {
  TDealerLocatorLandingFields,
  TDealerLocatorLandingProps,
  SearchType,
  TCountry,
  TDealershipResult,
  TFirebaseConfig,
  TDealerLocatorLandingFilters,
} from '../types/dealerLocatorLanding.types';

export class DealerLocatorLandingModel {
  private readonly fields?: TDealerLocatorLandingFields;
  private readonly sitecoreContext?: TSitecoreContext;
  filters: TDealerLocatorLandingFilters;

  constructor(props: TDealerLocatorLandingProps, sitecoreContext: TSitecoreContext) {
    this.fields = props.fields || undefined;
    this.sitecoreContext = sitecoreContext || undefined;
    this.filters = this.fields?.filters
      ? {
          Equipments: this.fields?.filters?.Equipments?.map((_) => ({ ..._, codeList: _.Code?.split(',') || [] })) ?? [],
          Services: this.fields?.filters?.Services?.map((_) => ({ ..._, codeList: _.Code?.split(',') || [] })) ?? [],
        }
      : { Equipments: [], Services: [] };
  }

  get title(): TStringField | undefined {
    return this.fields?.title;
  }

  get description(): TStringField | undefined {
    return this.fields?.description;
  }

  get googlaMapApiKey(): string {
    return this.fields?.settings?.dealerLocatorGoogleMapsAPIKey || '';
  }

  get initialLatitude(): number {
    return this.fields?.settings?.initialLatitude || 0.0;
  }

  get initialLongitude(): number {
    return this.fields?.settings?.initialLongitude || 0.0;
  }

  get initialZoom(): number {
    const zoom = this.fields?.settings?.initialZoom || 0;
    if (zoom < minZoomSize) return minZoomSize;
    if (zoom > maxZoomSize) return maxZoomSize;
    return zoom;
  }

  get pageId(): string {
    return this.fields?.pageId || '';
  }

  get siteLanguage(): string {
    return this.sitecoreContext?.language || '';
  }

  get brand(): string {
    return this.sitecoreContext?.brand || '';
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }

  get searchType(): SearchType {
    return this.fields?.settings?.dealerLocatorSearchType || SearchType.NoSearch;
  }

  get countries(): Array<TCountry> {
    return this.fields?.settings?.dealerLocatorCountryState ?? [];
  }

  get defaultCountry(): string | null {
    return this.fields?.settings?.dealerLocatorDefaultCountry || null;
  }

  get hasDefaultCountryPreselected(): boolean {
    return this.fields?.settings?.showPreselectCountry === true;
  }

  get searchByUserLocationEnabled(): boolean {
    return this.searchType !== SearchType.NoSearch && this.searchType !== SearchType.SearchByCountryAndRegion;
  }

  get searchRadius(): string | null {
    return this.fields?.settings?.searchRadius ?? null;
  }

  get distanceUnits(): 'km' | 'mi' | '' {
    if (this.searchRadius?.includes('km')) {
      return 'km';
    }

    if (this.searchRadius?.includes('mi')) {
      return 'mi';
    }

    return '';
  }

  get maxMapZoomForSingleResult(): number {
    const zoomLevel = this.fields?.settings?.maxZoomForSingleResult ?? MAX_ZOOM_FOR_SINGLE_RESULT;
    return zoomLevel > 0 && zoomLevel <= MAX_AVAILABLE_ZOOM_LEVEL ? zoomLevel : MAX_ZOOM_FOR_SINGLE_RESULT;
  }

  get noSearchDealerResults(): TDealershipResult[] {
    if (this.searchType !== SearchType.NoSearch) {
      return [];
    }

    return this.fields?.settings?.dealerSearchResult?.dealershipResults ?? [];
  }

  get currentPagePath(): string {
    const contextUrl = this.sitecoreContext?.currentPageUrl ?? '';

    if (contextUrl) {
      const contextUrlObj = new URL(contextUrl);
      return contextUrlObj.pathname;
    }

    return '';
  }

  get maxResultsCount(): number | null {
    return this.fields?.settings?.maxResultsCount ?? null;
  }

  get areFiltersNotEmpty(): boolean {
    // Todo: Replace this code with line no. 141 for Services Ref US-446445
    // return Number(this.filters?.Equipments?.length) > 0 || Number(this.filters?.Services?.length) > 0;
    // End Code comments US-446445
    return Number(this.filters?.Equipments?.length) > 0;
  }

  get firebaseConfig(): TFirebaseConfig | null {
    return this.fields?.settings?.firebaseConfiguration ?? null;
  }

  get hasInteractiveMap(): boolean {
    return (
      !!this.firebaseConfig &&
      !!this.fields?.settings?.enableInteractiveMap &&
      (this.searchType === SearchType.SearchByCountryAndRegion || this.searchType === SearchType.SearchByCountryAndLocationOrDealer)
    );
  }

  
  get hasClusters(): boolean {
    return (
      !!this.fields?.settings?.enableClusters &&
      (this.searchType === SearchType.SearchByCountryAndLocationOrDealer ||
        this.searchType === SearchType.SearchByLocationOrDealer)
    );
  }

  get hasClickableMap(): boolean {
    return (
      !!this.fields?.settings?.enableClickableMap &&
      (this.searchType === SearchType.SearchByCountryAndLocationOrDealer ||
        this.searchType === SearchType.SearchByLocationOrDealer)
    );
  }
}
